import React from 'react'
import LegalPageView from './LegalPageView'

export default function PrivacyPolicyView(): React.JSX.Element {
  return (
    <LegalPageView title="Privacy Policy" lastUpdated="July 25, 2026">
      <p>
        This Privacy Policy explains how 9nights (&ldquo;9nights,&rdquo; &ldquo;we,&rdquo;
        &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, and shares information when you use
        our website, apps, and related services (collectively, the &ldquo;Service&rdquo;). By using
        the Service, you agree to the collection and use of information in accordance with this
        policy.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We collect a few different categories of information:</p>
      <p>
        <strong>Information you give us.</strong> When you create an account, we collect your name,
        email address, and password (or, if you sign in with Google, basic profile information from
        that provider). When you use the Service, we collect the stories, drafts, wiki entries, and
        other content you write or upload, along with any profile details, comments, or messages you
        choose to add.
      </p>
      <p>
        <strong>Information we collect automatically.</strong> Like most online services, we
        automatically collect certain information about how you access and use the Service,
        including your IP address, browser and device type, pages viewed, links clicked, and the
        dates and times of your visits. We and our service providers may use cookies and similar
        technologies to collect some of this information — see &ldquo;Cookies and Similar
        Technologies&rdquo; below.
      </p>
      <p>
        <strong>Information from third parties.</strong> If you choose to sign in using a
        third-party account (for example, Google), that provider may share certain account
        information with us, such as your name, email address, and profile picture, consistent with
        your settings on that service.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>
          Provide, maintain, and improve the Service, including the AI co-writing and editing
          features;
        </li>
        <li>Create and secure your account, and authenticate your sign-ins;</li>
        <li>Store, organize, and display the stories and other content you create;</li>
        <li>Communicate with you about your account, updates, and support requests;</li>
        <li>Understand how the Service is used so we can fix bugs and improve features;</li>
        <li>Detect, investigate, and prevent fraud, abuse, and security incidents; and</li>
        <li>Comply with our legal obligations.</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>
        <strong>Content you choose to publish.</strong> If you publish a story, the story text,
        title, blurb, genre tags, and your author name become visible to anyone who has the link or
        finds it through the Service — that is the point of publishing. Draft and unpublished
        content is not shared with other users.
      </p>
      <p>
        <strong>Service providers.</strong> We share information with vendors who help us run the
        Service, such as hosting providers, database and infrastructure providers, email delivery
        services, and analytics providers. These providers are only permitted to use your
        information to perform services on our behalf.
      </p>
      <p>
        <strong>Legal reasons.</strong> We may disclose information if we believe in good faith that
        it is necessary to comply with a legal obligation, protect the rights or safety of 9nights,
        our users, or the public, or investigate potential violations of our Terms of Use.
      </p>
      <p>
        <strong>Business transfers.</strong> If 9nights is involved in a merger, acquisition, or
        sale of assets, your information may be transferred as part of that transaction. We will
        notify you of any such change and any choices you may have.
      </p>
      <p>We do not sell your personal information.</p>

      <h2>4. Cookies and Similar Technologies</h2>
      <p>
        We use cookies and similar technologies to keep you signed in, remember your preferences,
        and understand how the Service is used. You can control cookies through your browser
        settings; note that disabling cookies may affect the availability of certain features, such
        as staying signed in.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your account information and content for as long as your account is active, or as
        needed to provide you the Service. If you delete your account, we will delete or anonymize
        your personal information within a reasonable period, except where we are required to retain
        it for legal, security, or accounting purposes.
      </p>

      <h2>6. Your Rights and Choices</h2>
      <p>
        Depending on where you live, you may have the right to access, correct, export, or delete
        the personal information we hold about you, or to object to or restrict certain processing.
        You can update your account and profile details directly within the Service, or contact us
        using the details below to make a request. We will respond in accordance with applicable
        law.
      </p>

      <h2>7. Children&rsquo;s Privacy</h2>
      <p>
        The Service is not directed to children under 13 (or the equivalent minimum age in your
        jurisdiction), and we do not knowingly collect personal information from children. If you
        believe a child has provided us with personal information, please contact us and we will
        take steps to delete it.
      </p>

      <h2>8. International Users</h2>
      <p>
        We may process and store your information in countries other than the one in which you live.
        Wherever we process your information, we take reasonable steps to protect it consistent with
        this Privacy Policy.
      </p>

      <h2>9. Security</h2>
      <p>
        We use reasonable administrative, technical, and physical safeguards designed to protect
        your information. No method of transmission or storage is completely secure, however, and we
        cannot guarantee absolute security.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If we make material changes, we will
        notify you by posting the updated policy on this page and updating the &ldquo;Last
        updated&rdquo; date above.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or how we handle your information, contact
        us at <a href="mailto:privacy@9nights.app">privacy@9nights.app</a>.
      </p>
    </LegalPageView>
  )
}
